import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  "/img.png",
  "/img2.png",
  "/img3.png",
  "/img4.png",
];

export default function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Navigation functions
  const nextSlide = () => {
    setCurrent(current === images.length - 1 ? 0 : current + 1);
  };

  const prevSlide = () => {
    setCurrent(current === 0 ? images.length - 1 : current - 1);
  };

  // Touch swipe handlers for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const difference = touchStartX.current - touchEndX.current;
    
    // Minimum swipe distance (50px)
    if (Math.abs(difference) > 50) {
      if (difference > 0) {
        // Swipe left - next slide
        nextSlide();
      } else {
        // Swipe right - previous slide
        prevSlide();
      }
    }
    
    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Handle image load
  useEffect(() => {
    const imagePromises = images.map(src => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    Promise.all(imagePromises)
      .then(() => setIsLoading(false))
      .catch(() => setIsLoading(false));
  }, []);

  return (
    <section 
      className="relative w-full h-[40vh] sm:h-[60vh] md:h-[70vh] lg:h-[85vh] overflow-hidden bg-gradient-to-br from-orange-50 to-amber-50"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Loading skeleton for mobile */}
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-3 sm:border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
            <p className="text-orange-600 font-medium text-sm sm:text-base">Loading...</p>
          </div>
        </div>
      )}

      {/* Slides */}
      {images.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            index === current 
              ? "opacity-100 z-10 translate-x-0" 
              : index < current
                ? "opacity-0 -translate-x-full"
                : "opacity-0 translate-x-full"
          }`}
        >
          <div className="relative w-full h-full">
            {/* Conditional rendering for mobile vs desktop */}
            {isMobile ? (
              // MOBILE VIEW - No space, image fits exactly
              <div className="relative w-full h-auto flex items-center justify-center bg-black">
                <img
                  src={img}
                  alt={`Slide ${index + 1}`}
                  className="max-w-full max-h-full object-contain"
                  loading={index === 0 ? "eager" : "lazy"}
                  onError={(e) => {
                    console.error(`Failed to load image: ${img}`);
                    e.target.src = `https://placehold.co/400x400/111827/white?text=Hotel+Prakash+Palace`;
                  }}
                />
                {/* Light gradient overlay for mobile */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            ) : (
              // DESKTOP VIEW - Original styling (unchanged)
              <>
                <img
                  src={img}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-cover object-center md:object-center"
                  loading={index === 0 ? "eager" : "lazy"}
                  sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1024px) 100vw, 100vw"
                  onError={(e) => {
                    console.error(`Failed to load image: ${img}`);
                    e.target.src = `https://placehold.co/${window.innerWidth}x600/f97316/white?text=Image+${index+1}`;
                  }}
                />
                
                {/* Gradient overlay for better visibility on all screens */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent md:from-black/50 md:via-black/10 md:to-transparent"></div>
              </>
            )}
          </div>
        </div>
      ))}

      {/* Navigation Arrows - Hidden on very small screens, smaller on mobile */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-300 group active:scale-95"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white group-hover:scale-110 transition-transform" />
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all duration-300 group active:scale-95"
        aria-label="Next slide"
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white group-hover:scale-110 transition-transform" />
      </button>

      {/* Slide counter - Smaller on mobile */}
      <div className="absolute top-3 sm:top-4 right-3 sm:right-6 z-20 bg-black/50 backdrop-blur-sm text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
        {current + 1} / {images.length}
      </div>

      {/* DOTS - Adjusted for mobile */}
      <div className="absolute bottom-3 sm:bottom-4 md:bottom-6 lg:bottom-10 left-1/2 -translate-x-1/2 z-30">
        <div className="flex gap-1.5 sm:gap-2 md:gap-3">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`transition-all duration-300 active:scale-110 ${
                current === index
                  ? "bg-orange-500 shadow-lg shadow-orange-500/50"
                  : "bg-white/80 hover:bg-white"
              } ${
                isMobile 
                  ? "w-2 h-2 rounded-full" 
                  : "w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded-full"
              } ${
                current === index && !isMobile ? "scale-125" : ""
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

